import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUrlString, useUrlDateRange, useUrlNumberOpt, useUrlNumber } from '../../hooks/useUrlState';
import { useConfirmedMutation } from '../../lib/confirm';
// The shared `Field`, not a local label-wrapper: wrapping a Select's trigger in a <label> left the control announced
// as its own value ("2026-27") instead of "Financial year" — the kit version mints the id and the accessible name.
import { Table, Select, Input, Textarea, InputNumber, Segmented, Typography, Button, Modal, message, DatePicker, StatCard, statTone, Counted, CodeChip, entityHue, EntityAvatar, Skeleton, Field, cn } from '../../ui';
import { PackageX, Undo2, Search, Clock, Plus, TrendingUp, Wallet, Download, CalendarClock, Store, IndianRupee, CircleDot, MapPin } from 'lucide-react';
import { request, api } from '../../api/client';
import { useFocusRow } from '../../hooks/useFocusRow';
import { useDebounce } from '../../hooks/useDebounce';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';
import { useServerSort } from '../../hooks/useServerSort';
import { Pill } from '../../components/TwDataTable';
import { RetailerLink } from '../../components/EntityLinks';
import { CreditNoteModal } from '../../components/CreditNoteModal';
import { ExportDialog } from '../../components/ExportDialog';
import { FilterRange, useFilterPanel, type FilterChip } from '../../components/FilterPanel';
import { TodRunDrawer } from './TodRunDrawer';
import { disableFromDate, disableToDate } from '../../lib/dateRange';
import { usePermissionChecker } from '../../store/auth';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { IDENTITY_SERIES } from '../../lib/palette';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;

/** Rows per page until the reader picks another size — 20, the house default every register pages at.
 *  The server slices the register to it, so it is also the size of every fetch; 200 is the most the
 *  API will hand over at once. */
const PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 200;

const STATUS_PILL: Record<string, string> = { POSTED: 'green', DRAFT: 'gold', CANCELLED: 'red' };
/** One map behind the Status column, the Status filter and the filter chips, so they cannot drift. */
const STATUS_LABEL: Record<string, string> = { POSTED: 'Issued', DRAFT: 'Pending approval', CANCELLED: 'Reversed' };

/**
 * What a row IS — the register's real vocabulary, and the one thing the Type column should say.
 *
 * It used to print "Credit" on every line (plus a second "Bonus" pill on a few), which is the note's
 * accounting direction, not its kind: a column whose value is the same on 29 of 30 rows is a column
 * nobody can read anything off. A note is credit raised against a sales return, a standalone
 * turnover bonus, or the compensating debit a reversal writes back.
 */
type Kind = 'ALL' | 'RETURN' | 'TURNOVER_BONUS' | 'DEBIT';
const KIND_FILTER: Record<Exclude<Kind, 'ALL'>, Record<string, string>> = {
  RETURN: { type: 'CREDIT', source: 'RETURN' },
  TURNOVER_BONUS: { type: 'CREDIT', source: 'TURNOVER_BONUS' },
  DEBIT: { type: 'DEBIT' },
};
const kindOf = (r: any): Exclude<Kind, 'ALL'> =>
  (r.noteType !== 'CREDIT' ? 'DEBIT' : r.source === 'TURNOVER_BONUS' ? 'TURNOVER_BONUS' : 'RETURN');
const KIND_PILL: Record<Exclude<Kind, 'ALL'>, { label: string; color: string }> = {
  RETURN: { label: 'Return credit', color: 'green' },
  TURNOVER_BONUS: { label: 'Turnover bonus', color: 'blue' },
  DEBIT: { label: 'Reversal debit', color: 'gold' },
};

// Indian financial year (Apr–Mar). Returns e.g. "2026-27" for a date in/after April 2026.
function fyOf(d: Date): string {
  const y = d.getFullYear();
  const start = d.getMonth() >= 3 ? y : y - 1; // month 3 = April (0-indexed)
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}
const FY_OPTIONS = (() => {
  const now = new Date();
  const cur = fyOf(now);
  const startYr = Number(cur.slice(0, 4));
  return [0, 1, 2].map((back) => {
    const s = startYr - back;
    return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
  });
})();

/**
 * Credit-note REGISTER (Accounts / Admin) — every credit note the system has issued: sales-return credit notes
 * AND standalone turnover/volume BONUS credits (a financial incentive a retailer earns for their annual
 * business). Filterable by status / date / retailer; a row opens the canonical note drawer (over this page).
 * Bonuses are maker-checker: drafted here ("New turnover bonus"), then approved/reversed from the drawer.
 * Debit entries are only the compensating debits from a reversal — they stay visible + traceable but debit is
 * not a first-class issuable type.
 */
export function CreditNotesPage() {
  const hasPerm = usePermissionChecker();
  const qc = useQueryClient();
  const canCreateBonus = hasPerm('CREDIT_NOTE.BONUS.CREATE');
  // Server gate is INVOICE.EXPORT — deliberately NOT INVOICE.VIEW, which a retailer holds to follow
  // their own notes. This register lists every dealer's credit with the GST split.
  const canExport = hasPerm('INVOICE.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);

  /*
   * ONE filter over what a note IS, replacing an "All / Credit" toggle that could not tell you
   * anything: debit notes are only the compensating debits a reversal writes, so "Credit" was very
   * nearly the whole register and the two segments returned almost the same list. The register
   * actually holds three KINDS, and these are they.
   */
  const [kind, setKind] = useUrlString<Kind>('kind', 'ALL');
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  /* TWO INDEPENDENT DATES, not a RangePicker.
     The kit's RangePicker only ever emits a COMPLETE range, so "everything since 1 April" silently
     did nothing on a register whose server applies each bound on its own. */
  const [range, setRange] = useUrlDateRange('range');
  const [retailerId, setRetailerId] = useUrlString<string | undefined>('retailer', undefined);
  const [financialYear, setFinancialYear] = useUrlString<string | undefined>('fy', undefined);
  // The year a turnover bonus was EARNED in — distinct from the note's own FY (`fy`, from its date).
  const [earnedFy, setEarnedFy] = useUrlString<string | undefined>('efy', undefined);
  const [minAmount, setMinAmount] = useUrlNumberOpt('min');
  const [maxAmount, setMaxAmount] = useUrlNumberOpt('max');
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  /* The page is part of the view, so it lives in the URL like the filters do — open a note, press
     Back, and you return to the page you were reading rather than the top of the register. */
  const [page, setPage] = useUrlNumber('page', 1);
  /* The size changer is part of the pager the table kit draws, so the size has to be real state — a
     control that cannot change anything is worse than no control. Clamped on the way out: the API
     refuses more than 200 a page, and the pager must not believe it asked for more than it got. */
  const [pageSizeRaw, setPageSize] = useUrlNumber('pageSize', PAGE_SIZE);
  const pageSize = Math.min(Math.max(Math.trunc(pageSizeRaw) || PAGE_SIZE, 5), MAX_PAGE_SIZE);
  const [noteId, setNoteId] = useState<string | null>(null);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [todOpen, setTodOpen] = useState(false);
  const [creditInHandOpen, setCreditInHandOpen] = useState(false);
  // Server-side column sort (backend ORDER BY over the whole filtered set, not just the displayed page).
  const { sortState, onSortChange, sort } = useServerSort();
  const canViewRetailers = hasPerm('RETAILER.VIEW');

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (kind !== 'ALL') Object.assign(p, KIND_FILTER[kind]);
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (retailerId) p.retailerId = retailerId;
    if (financialYear) p.financialYear = financialYear;
    if (earnedFy) p.earnedFinancialYear = earnedFy;
    if (minAmount != null) p.minAmount = minAmount;
    if (maxAmount != null) p.maxAmount = maxAmount;
    // Each end alone: "since 1 April" and "up to 30 June" are both whole questions.
    if (range[0]) p.from = range[0].format('YYYY-MM-DD');
    if (range[1]) p.to = range[1].format('YYYY-MM-DD');
    if (sort) p.sort = sort;
    return p;
  }, [kind, status, search, retailerId, financialYear, earnedFy, minAmount, maxAmount, range, sort]);

  /* Dealer options for the Retailer filter, searched on the server; a URL-selected dealer is fetched by
     id so its chip and the export scope still name it.
     Hidden from anyone without RETAILER.VIEW: a picker of dealer names somebody may not see is a
     leak, not a filter. */
  const retailerPick = useRetailerSearch({ key: 'retailers-for-bonus', enabled: canViewRetailers, selected: retailerId });
  const retailerList: any[] = retailerPick.rows;
  const retailerName = (id: string) => {
    const r = retailerPick.rowFor(id);
    return r ? (r.companyName ?? r.name ?? `#${id}`) : `#${id}`;
  };

  /*
   * THE PAGE IS NOT A FILTER, AND THE EXPORT MUST NOT INHERIT IT.
   *
   * `params` stays exactly "what the reader is looking at" — it is what the tiles are counted from
   * and what the Export dialog sends. The page number is added HERE, for the list fetch alone, so a
   * file can never come out holding the 25 rows that happened to be on screen. (The server drops a
   * stray page off an export URL too; neither side relies on the other remembering.)
   */
  const listParams = useMemo(() => ({ ...params, page, pageSize }), [params, page, pageSize]);
  const { data, isFetching } = useQuery({
    queryKey: ['credit-notes-register', listParams],
    queryFn: () => request<any>({ url: '/credit-debit-notes', params: listParams }),
    // Keeps the page you are leaving on screen while the next one loads, instead of blanking the table.
    placeholderData: (prev) => prev,
  });
  const rows = data?.rows ?? [];
  const total: number = Number(data?.total ?? 0);

  /*
   * A FILTER CHANGE INVALIDATES THE PAGE NUMBER — page 4 of "every note" is not page 4 of "reversed
   * bonuses", and a sort reorders the whole set so page 4 of it is a different 25 rows.
   *
   * Keyed off the serialised filters rather than a dependency list of nine values, one of which is a
   * pair of dayjs objects that are new on every render (that list would reset the page on every
   * keystroke and the pager could never leave page 1). The first key is skipped so a link INTO page 3
   * — or Back restoring it — is not thrown away the moment the page mounts.
   */
  const filterKey = useMemo(() => JSON.stringify(params), [params]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* A page past the end of the register — a stale bookmark, or notes deleted since — is a blank
     table under a pager that says there are 183 of them. Land on the last real page instead. */
  useEffect(() => {
    if (isFetching || !total || rows.length || page <= 1) return;
    setPage(Math.max(1, Math.ceil(total / pageSize)));
  }, [isFetching, total, rows.length, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The tiles read from their OWN fetch, holding every filter the reader set — period, dealer, FY,
     amount, note search — but dropping the KIND and STATUS the tiles themselves set. Off the filtered
     response they would collapse the moment you used one: click "Reversed" and every other tile reads
     0, so the band could never tell you what you were drilling out of. Keeping the rest is what lets
     a tile stay honest inside a filter — "Turnover bonus credit" under a dealer filter is that
     dealer's bonuses, and clicking it lands on exactly those rows. */
  const kpiParams = useMemo(() => {
    const p = { ...params };
    delete p.type; delete p.source; delete p.status; delete p.sort;
    // This call is read for its SUMMARY and its financial-year list, never for its rows — both are
    // computed across the whole filtered set, so asking for one row rather than the default cap
    // makes the second fetch cost nothing.
    return { ...p, page: 1, pageSize: 1 };
  }, [params]);
  const { data: kpiData } = useQuery({
    queryKey: ['credit-notes-kpis', kpiParams],
    queryFn: () => request<any>({ url: '/credit-debit-notes', params: kpiParams }),
  });
  /* The years the FY filter may offer, from the server rather than counted forward from today: a
     bonus can be raised for a year older than the three this page can compute, and an option list
     that cannot express what the data holds is a filter that lies. Scope-wide, never narrowed by the
     current filters, so picking one year does not leave the others unreachable. */
  const fyOptions: string[] = kpiData?.financialYears?.length ? kpiData.financialYears
    : data?.financialYears?.length ? data.financialYears : FY_OPTIONS;
  // A deep-link from the approvals hub names the note it meant (?focus=…) — open its drawer, which
  // is this page's detail view, instead of dropping the reader at the top of the register.
  /* `onMissing` is what keeps the approvals hub's `?focus=<id>` working now that the register is
     paged: the note it names is usually NOT among the 25 rows in hand, and the drawer loads a note
     by id anyway. Without it the link would quietly drop its own parameter and leave the reader at
     the top of the register hunting for the bonus they clicked. */
  const focus = useFocusRow(rows, {
    match: ['id', 'noteNo'],
    onFocus: (r) => setNoteId(String(r.id)),
    onMissing: (wanted) => setNoteId(wanted),
  });
  const s = kpiData?.summary ?? {
    creditCount: 0, creditAmt: 0, debitCount: 0, debitAmt: 0, reversedCount: 0, reversedAmt: 0, pendingCount: 0, pendingAmt: 0,
    returnCount: 0, returnAmt: 0, bonusCount: 0, bonusAmt: 0, creditInHand: 0, creditInHandDealers: 0,
  };
  /*
   * ONE TILE = ONE DRILL, AND THE DRILL OPENS EXACTLY THE TILE'S OWN ROWS.
   *
   * "Turnover bonuses / 19 posted" opened 27 rows: the tile counted POSTED bonuses and its click
   * filtered on the KIND alone, so the eight reversed bonuses the figure had deliberately left out
   * came back with them. A tile is a promise about a set of rows — every drill now carries BOTH halves
   * of the definition its figure was counted from.
   *
   * A drill REPLACES the kind/status pair rather than stacking onto it, and clicking the tile you are
   * already in steps back out, so the band is the way out as well as the way in. The reader's other
   * filters (dealer, period, FY, amount) survive, because the tile's own figure was counted inside
   * them. A tile with nothing behind it is not a control at all.
   */
  const isDrill = (next: { kind?: Kind; status?: string }) =>
    kind === (next.kind ?? 'ALL') && (next.status === undefined ? !status : status === next.status);
  const drill = (next: { kind?: Kind; status?: string }) => () => {
    if (isDrill(next)) { setKind('ALL'); setStatus(undefined); return; }
    setKind(next.kind ?? 'ALL');
    setStatus(next.status);
  };

  const refetchRegister = () => qc.invalidateQueries({ queryKey: ['credit-notes-register'] });

  /* Said in the export dialog, because a file that claims to be filtered and is not is worse than no
     export at all — and once the dialog is open the toolbar behind it can no longer be read. */
  const exportScope = useMemo(() => {
    const bits = [
      kind === 'ALL' ? null : kind === 'RETURN' ? 'return credits' : kind === 'TURNOVER_BONUS' ? 'turnover bonuses' : 'reversal debits',
      status ? (STATUS_LABEL[status] ?? status).toLowerCase() : null,
      retailerId ? retailerName(retailerId) : null,
      financialYear ? `FY ${financialYear}` : null,
      earnedFy ? `earned in FY ${earnedFy}` : null,
      range[0] || range[1] ? 'the chosen dates' : null,
      minAmount != null || maxAmount != null ? 'the chosen amounts' : null,
      search.trim() ? `"${search.trim()}"` : null,
    ].filter(Boolean);
    return bits.length ? `Filtered to ${bits.join(' · ')}, in the order shown on screen.`
      : 'Every note in your scope, in the order shown on screen.';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, status, retailerId, financialYear, earnedFy, range, minAmount, maxAmount, search, retailerList]);

  /* ── The filters ───────────────────────────────────────────────────────────────────────────
   * Four of these were already applied by the server and had no control on the screen — a dealer,
   * a financial year, an amount band and each end of the period on its own. A dashboard could link
   * here filtered to one dealer and nothing on this page would say so.
   *
   * Seven controls do not fit on one toolbar, so the two you steer with — what a note IS, and the
   * box you type a document number into — stay inline, and the rest open IN PLACE underneath in
   * sections that follow the COLUMNS. What is applied shows as removable chips in the toolbar,
   * whether the panel is open or shut.
   */
  const clearFilters = () => {
    setStatus(undefined); setRange([null, null]); setRetailerId(undefined);
    setFinancialYear(undefined); setEarnedFy(undefined); setMinAmount(undefined); setMaxAmount(undefined);
  };
  const fmtDay = (d: any) => (d ? d.format('DD MMM YY') : null);
  const chips = [
    status && { key: 'status', label: `Status: ${STATUS_LABEL[status] ?? status}`, onRemove: () => setStatus(undefined) },
    (range[0] || range[1]) && {
      key: 'range',
      label: range[0] && range[1] ? `${fmtDay(range[0])} – ${fmtDay(range[1])}`
        : range[0] ? `On or after ${fmtDay(range[0])}` : `On or before ${fmtDay(range[1])}`,
      onRemove: () => setRange([null, null]),
    },
    financialYear && { key: 'fy', label: `FY ${financialYear}`, onRemove: () => setFinancialYear(undefined) },
    earnedFy && { key: 'efy', label: `Earned in FY ${earnedFy}`, onRemove: () => setEarnedFy(undefined) },
    retailerId && { key: 'retailer', label: `Dealer: ${retailerName(retailerId)}`, onRemove: () => setRetailerId(undefined) },
    (minAmount != null || maxAmount != null) && {
      key: 'amount',
      label: minAmount != null && maxAmount != null ? `${inr(minAmount)} – ${inr(maxAmount)}`
        : minAmount != null ? `${inr(minAmount)} and above` : `Up to ${inr(maxAmount)}`,
      onRemove: () => { setMinAmount(undefined); setMaxAmount(undefined); },
    },
  ].filter(Boolean) as FilterChip[];

  const filters = useFilterPanel({
    chips,
    onClear: clearFilters,
    groups: [
      {
        title: 'When',
        tone: IDENTITY_SERIES[2],
        icon: <CalendarClock size={12} />,
        fields: [
          {
            label: 'Note date',
            span: 2,
            /* The kit's date field is a BUTTON, and a wrapping <label> does not name a button — so
               each half carries its own aria-label. Either end alone is a complete question. */
            control: (
              <FilterRange
                from={<DatePicker value={range[0]} placeholder="From" aria-label="Note date from"
                  disabledDate={disableFromDate(range[1])} onChange={(d) => setRange([d, range[1]])} />}
                to={<DatePicker value={range[1]} placeholder="To" aria-label="Note date to"
                  disabledDate={disableToDate(range[0])} onChange={(d) => setRange([range[0], d])} />}
              />
            ),
          },
          {
            label: 'Financial year',
            hint: 'The year the note is dated in.',
            control: (
              <Select
                placeholder="Any year" allowClear value={financialYear}
                onChange={(v) => setFinancialYear((v as string) || undefined)}
                options={fyOptions.map((fy) => ({ value: fy, label: `FY ${fy}` }))}
              />
            ),
          },
          {
            label: 'Earned in FY',
            hint: 'The year a turnover bonus was earned for.',
            control: (
              <Select
                placeholder="Any year" allowClear value={earnedFy}
                onChange={(v) => setEarnedFy((v as string) || undefined)}
                options={fyOptions.map((fy) => ({ value: fy, label: `FY ${fy}` }))}
              />
            ),
          },
        ],
      },
      ...(canViewRetailers ? [{
        title: 'Who',
        tone: IDENTITY_SERIES[1],
        icon: <Store size={12} />,
        fields: [{
          label: 'Dealer',
          span: 2 as const,
          control: (
            <Select
              placeholder="Any dealer" allowClear showSearch optionFilterProp="searchLabel"
              filterOption={false} onSearch={retailerPick.onSearch}
              popupMatchSelectWidth={false}
              value={retailerId} onChange={(v) => setRetailerId((v as string) || undefined)}
              options={retailerList.map((r: any) => ({
                value: String(r.id),
                searchLabel: `${r.companyName ?? r.name ?? ''} ${r.code ?? ''}`.trim(),
                label: (
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{r.companyName ?? r.name ?? `#${r.id}`}</span>
                    {r.code && <CodeChip hue={entityHue(r.code)}>{r.code}</CodeChip>}
                  </span>
                ),
              }))}
            />
          ),
        }],
      }] : []),
      {
        title: 'How much',
        tone: IDENTITY_SERIES[0],
        icon: <IndianRupee size={12} />,
        fields: [{
          label: 'Note amount',
          span: 2,
          hint: 'Either end on its own is a whole question.',
          control: (
            <FilterRange
              from={<InputNumber min={0} placeholder="e.g. 1000" aria-label="Minimum note amount"
                value={minAmount ?? null} onChange={(v) => setMinAmount(v == null ? undefined : Number(v))} />}
              to={<InputNumber min={0} placeholder="e.g. 50000" aria-label="Maximum note amount"
                value={maxAmount ?? null} onChange={(v) => setMaxAmount(v == null ? undefined : Number(v))} />}
            />
          ),
        }],
      },
      {
        title: 'Where it stands',
        tone: IDENTITY_SERIES[3],
        icon: <CircleDot size={12} />,
        fields: [{
          label: 'Status',
          control: (
            <Select
              placeholder="Any status" allowClear value={status}
              onChange={(v) => setStatus((v as string) || undefined)}
              options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
            />
          ),
        }],
      },
    ],
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Typography.Title level={1} style={{ margin: 0 }}>Credit Notes</Typography.Title>
        <div className="flex flex-wrap gap-2">
          {canExport && (
            <Button icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          {canCreateBonus && (
            <>
              <Button variant="default" icon={<TrendingUp size={16} />} onClick={() => setTodOpen(true)}>Run turnover discount</Button>
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => setBonusOpen(true)}>New turnover bonus</Button>
            </>
          )}
        </div>
      </div>

      {/*
        * THE BAND IS A PARTITION OF THE REGISTER, PLUS ONE LIVE BALANCE.
        *
        * Issued-for-returns + issued-as-bonus + waiting + reversed accounts for every row the list can
        * hold, so the four figures can be added up and checked against it. It used to be a NESTING
        * instead — a headline "Credit notes ₹35,285" whose hint ("₹3,485 returns + ₹31,800 bonuses")
        * was the only place the returns figure appeared at all. A number inside a hint is not a figure:
        * it cannot be clicked, and the two halves are earned differently, approved differently and
        * reported on separately. Each is now its own tile, and each opens its own rows.
        *
        * Labels say what the money IS ("Credit for returned goods"), not what the column is called; the
        * one tile whose value is a count carries the word "notes" beside it, since on a band of rupee
        * figures a bare 13 reads as an amount; and every hint is the same kind of thing — one figure
        * and the plain fact behind it.
        */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi
          title="Credit for returned goods" value={inr(s.returnAmt)}
          sub={s.returnCount
            ? `${s.returnCount} note${s.returnCount === 1 ? '' : 's'} · for stock a dealer sent back`
            : 'no return credit issued in this period'}
          accent="border-l-success-500" icon={<PackageX size={16} />}
          active={isDrill({ kind: 'RETURN', status: 'POSTED' })}
          onClick={s.returnCount ? drill({ kind: 'RETURN', status: 'POSTED' }) : undefined}
        />
        <Kpi
          title="Turnover bonus credit" value={inr(s.bonusAmt)}
          sub={s.bonusCount
            ? `${s.bonusCount} note${s.bonusCount === 1 ? '' : 's'} · a reward for the year's purchases`
            : 'no bonus issued in this period'}
          accent="border-l-sky-500" icon={<TrendingUp size={16} />}
          active={isDrill({ kind: 'TURNOVER_BONUS', status: 'POSTED' })}
          onClick={s.bonusCount ? drill({ kind: 'TURNOVER_BONUS', status: 'POSTED' }) : undefined}
        />
        <Kpi
          title="Waiting for approval" value={inr(s.pendingAmt)}
          sub={s.pendingCount
            ? `${s.pendingCount} bonus${s.pendingCount === 1 ? '' : 'es'} · drafted, not on any ledger yet`
            : 'nothing is waiting for approval'}
          accent="border-l-warning-500" icon={<Clock size={16} />}
          active={isDrill({ status: 'DRAFT' })}
          onClick={s.pendingCount ? drill({ status: 'DRAFT' }) : undefined}
        />
        <Kpi
          title="Reversed and cancelled" value={<Counted n={s.reversedCount} one="note" many="notes" />}
          sub={s.reversedCount
            ? `${inr(s.reversedAmt)} · taken back off dealer ledgers`
            : 'nothing has been reversed'}
          accent="border-l-danger-500" icon={<Undo2 size={16} />}
          active={isDrill({ status: 'CANCELLED' })}
          onClick={s.reversedCount ? drill({ status: 'CANCELLED' }) : undefined}
        />
        {/* Unused advance still sitting on dealer accounts — where a partially-used credit's remainder
            lives. The one tile that is NOT a slice of this register: it is a live balance summed across
            retailer accounts (the same Σ max(0, −outstanding_balance) the ledger uses), so there is no
            set of NOTES for it to open. It is still the question "which dealers?", so it answers with
            the dealers rather than staying the one figure on the band nobody could act on. */}
        <Kpi
          title="Unused credit with dealers" value={inr(s.creditInHand)}
          sub={s.creditInHandDealers
            ? `${s.creditInHandDealers} dealer${s.creditInHandDealers === 1 ? '' : 's'} · can still spend it on a future order`
            : 'no dealer is carrying unused credit'}
          accent="border-l-brand-500" icon={<Wallet size={16} />}
          active={creditInHandOpen}
          onClick={s.creditInHandDealers ? () => setCreditInHandOpen(true) : undefined}
        />
      </div>

      {/* Filters — what a note IS and the box you type a document number into stay on the toolbar;
          everything else is behind the panel, with what is applied shown as chips beside it. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface shadow-e1 px-3 py-2.5">
        <Segmented
          value={kind}
          onChange={(v) => setKind(String(v) as Kind)}
          options={[
            { label: 'All', value: 'ALL' },
            { label: 'Returns', value: 'RETURN' },
            { label: 'Bonuses', value: 'TURNOVER_BONUS' },
            { label: 'Reversal debits', value: 'DEBIT' },
          ]}
        />
        {/* The box now searches every identifier ON the row. It sat beside a Retailer column and an
            Against-invoice column and matched neither, so a dealer's name returned "no notes match"
            on a register holding twenty of theirs. */}
        <Input
          prefix={<Search size={16} />} placeholder="Note, invoice, return or dealer…"
          aria-label="Search notes" className="w-64"
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
        />
        {filters.trigger}
        {filters.chips}
      </div>
      {filters.panel}

      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <Table
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={rows}
          rowClassName={focus.rowClassName}
          sortState={sortState}
          onSortChange={onSortChange}
          /* Server-driven: `total` is every note matching the filters, not the number in hand, so the
             pager states the real size of the register and reaches the oldest note in it. */
          pagination={{
            current: page,
            pageSize,
            total,
            /* The kit's default reads "43 rows", which names neither what a row is nor where in the
               register you are standing. Both, in the house wording. */
            showTotal: (t: number, [from, to]: [number, number]) => `${from}–${to} of ${t.toLocaleString('en-IN')} note${t === 1 ? '' : 's'}`,
            // A bigger page is a different page 1, never page 4 of the old size.
            onChange: (p: number, ps: number) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else setPage(p); },
          }}
          scroll={{ x: 'max-content' }}
          onRow={(r: any) => ({ onClick: () => setNoteId(String(r.id)), style: { cursor: 'pointer' } })}
          locale={{ emptyText: 'No credit notes match these filters' }}
          columns={[
            { title: 'Note No', dataIndex: 'noteNo', width: 160, sorter: true, render: (v: string) => <span className="font-medium text-brand-700 dark:text-brand-400">{v}</span> },
            {
              title: 'Type', dataIndex: 'noteType', width: 150, sorter: true,
              render: (_: string, r: any) => {
                const k = KIND_PILL[kindOf(r)];
                return <Pill color={k.color}>{k.label}</Pill>;
              },
            },
            // Human dates — the register printed "2026-09-15" (E2E 15 Sep).
            { title: 'Date', dataIndex: 'noteDate', width: 110, sorter: true, render: (v: any) => <span className="whitespace-nowrap">{formatIstDate(v)}</span> },
            { title: 'Retailer', dataIndex: 'retailerName', width: 230, sorter: true, render: (v: string, r: any) => (v ? <span>{r.retailerCode ? <span className="text-subtle">{r.retailerCode} · </span> : ''}<RetailerLink id={r.retailerId}>{v}</RetailerLink></span> : `#${r.retailerId}`) },
            { title: 'Against invoice', dataIndex: 'invoiceNo', width: 150, render: (v: string, r: any) => v || (r.source === 'TURNOVER_BONUS' ? <span className="text-subtle">Earned in FY {r.earnedFinancialYear ?? r.financialYear ?? '—'}</span> : '—') },
            { title: 'Return', dataIndex: 'returnNo', width: 130, render: (v: string) => v || '—' },
            { title: 'Amount', dataIndex: 'amount', align: 'right' as const, width: 120, sorter: true, render: (v: any, r: any) => <span className={r.status === 'CANCELLED' ? 'text-subtle line-through' : 'font-semibold text-content'}>{inr2(v)}</span> },
            { title: 'Status', dataIndex: 'status', width: 100, sorter: true, render: (v: string) => <Pill color={STATUS_PILL[v] ?? 'default'}>{v === 'CANCELLED' ? 'Reversed' : v === 'POSTED' ? 'Issued' : v === 'DRAFT' ? 'Pending' : v}</Pill> },
          ]}
        />
      </div>

      <CreditInHandModal open={creditInHandOpen} onClose={() => setCreditInHandOpen(false)} />
      <CreditNoteModal id={noteId} onClose={() => setNoteId(null)} onChanged={refetchRegister} />
      <BonusModal open={bonusOpen} onClose={() => setBonusOpen(false)} onCreated={() => { setBonusOpen(false); refetchRegister(); }} />
      <TodRunDrawer open={todOpen} onClose={() => setTodOpen(false)} onGenerated={() => { setTodOpen(false); refetchRegister(); }} />
      {/*
        * THE FILE IS THE VIEW.
        *
        * The dialog used to own the period, and the page stripped its own `from`/`to` out of
        * `baseParams` to make room — so the reader set a date range on the toolbar, opened Export,
        * and was asked for the period again by a control that would win. Two pickers for one fact
        * can only ever disagree. `hidePeriod` removes the dialog's copy, which is exactly what that
        * flag exists for: the page's own dates then pass straight through.
        *
        * Everything else goes with them — kind, status, dealer, financial year, amount band, the
        * note search AND the column the reader sorted by, since the order a register is read in is
        * part of the view a file claims to be.
        */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export credit / debit notes"
        description={`The GST note trail: kind, the invoice or return it was raised against, the tax split and current status. ${exportScope}`}
        baseUrl="/credit-debit-notes/export"
        baseParams={params}
        hidePeriod
      />
    </div>
  );
}

/**
 * WHICH DEALERS are holding the unused credit — the rows behind the one tile that is not a slice of
 * this register.
 *
 * "Credit in hand ₹11,327" was a live balance summed across retailer accounts, so it had no set of
 * notes to open and sat on the band as the single figure nobody could act on. It is still a question,
 * though — "who is holding it?" — and the answer is a list of dealers, not a list of notes. Read-only
 * on purpose: a KPI answers a question, it does not start a transaction.
 *
 * Sums to the tile exactly: the same Σ max(0, −outstanding_balance) over the same retailer scope.
 */
function CreditInHandModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['credit-in-hand-dealers'],
    queryFn: () => request<any>({ url: '/credit-debit-notes/credit-in-hand' }),
    enabled: open,
  });
  const rows: any[] = data?.rows ?? [];
  // The band's own total, so every share printed on the list is a fraction of the figure above it.
  const total = Number(data?.total ?? 0);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      autoFocus={false}
      title="Unused credit with dealers"
      description="Advance still sitting on a dealer's account — from a credit note, a turnover bonus or an overpayment. They can spend it on a future order; nobody has to pay it out."
      width={620}
      footer={<Button variant="primary" onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : !rows.length ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-8 text-center">
          <span className="grid size-11 place-items-center rounded-pill bg-surface-3 text-subtle"><Wallet size={20} /></span>
          <span className="type-body text-muted">No dealer is carrying unused credit.</span>
        </div>
      ) : (
        <>
          {/* THE WHOLE OF IT, SAID ONCE. The figure the tile was clicked for, on the brand's own wash,
              with the fact behind it underneath — so the reader lands on the total and the list below
              is free to be nothing but the breakdown. Credit is money the dealer is OWED, so it is
              green: the house colouring for a balance in the dealer's favour (red is what they owe us). */}
          <div className="mb-3 flex items-center gap-3.5 rounded-card border border-brand-500/25 bg-linear-to-br from-brand-500/[0.14] via-brand-500/[0.05] to-transparent px-4 py-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-control border border-brand-500/30 bg-brand-500/12 text-brand-600 dark:text-brand-400">
              <Wallet size={20} />
            </span>
            <div className="min-w-0">
              <div className="type-metric text-brand-700 dark:text-brand-400">{inr2(data?.total ?? 0)}</div>
              <div className="type-label text-muted">
                held by {rows.length} dealer{rows.length === 1 ? '' : 's'} · largest holder first
              </div>
            </div>
          </div>
          {/*
            * EACH DEALER WEARS THEIR OWN COLOUR, AND THE ROW SHOWS THE SHARE.
            *
            * Seven names and seven rupee figures in a grey column answer "who?" but not "how
            * concentrated?" — and that is the fact worth seeing here, because one dealer is usually
            * holding most of it. The bar is each holding as a fraction of the total above, so the bars
            * add up to that band; the percentage is printed beside it rather than left to be judged by
            * eye, and a sliver keeps a tiny holding visible without claiming more width than it has.
            *
            * The mark and the rail take the dealer's identity hue (ui/EntityAvatar) — the same colour
            * that dealer wears on every other register, and identity only, never status.
            */}
          <div className="max-h-96 divide-y divide-line overflow-auto rounded-card border border-line">
            {rows.map((r) => {
              const name = r.retailerName ?? `#${r.retailerId}`;
              const hue = entityHue(name);
              const amt = Number(r.credit) || 0;
              const share = total > 0 ? (amt / total) * 100 : 0;
              return (
                <div key={String(r.retailerId)} className="relative flex items-center gap-3 py-2.5 pl-4 pr-3 transition-colors hover:bg-surface-2">
                  <span aria-hidden className="absolute inset-y-0 left-0 w-[3px]" style={{ background: `color-mix(in srgb, ${hue} 70%, transparent)` }} />
                  <EntityAvatar name={name} square size={34} />
                  <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <RetailerLink id={String(r.retailerId)}>{name}</RetailerLink>
                      {r.retailerCode && <CodeChip hue={entityHue(r.retailerCode)}>{r.retailerCode}</CodeChip>}
                      {r.city && (
                        <span className="inline-flex items-center gap-1 type-caption text-subtle">
                          <MapPin size={11} />{r.city}
                        </span>
                      )}
                    </span>
                    <span aria-hidden className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-3">
                      <span
                        className="block h-full rounded-pill"
                        style={{
                          width: `${Math.max(share, 1.5)}%`,
                          background: `linear-gradient(90deg, color-mix(in srgb, ${hue} 45%, transparent), ${hue})`,
                        }}
                      />
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block type-metric-sm text-brand-700 dark:text-brand-400">{inr2(amt)}</span>
                    <span className="block type-caption tabular-nums text-subtle">
                      {share >= 1 ? Math.round(share) : '<1'}% of it
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}

// ── "New turnover bonus" — drafts a standalone financial incentive credit (awaits Admin/SA approval) ──
function BonusModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [amount, setAmount] = useState<number | null>(null);
  const [financialYear, setFinancialYear] = useState<string>(FY_OPTIONS[0]);
  const [reason, setReason] = useState('');

  // Searched on the server; the picked retailer stays listed when a later search does not return it.
  const retailerPick = useRetailerSearch({ key: 'retailers-for-bonus', enabled: open, selected: retailerId });
  const retailers: any[] = retailerPick.rows;

  const reset = () => { setRetailerId(undefined); setAmount(null); setReason(''); setFinancialYear(FY_OPTIONS[0]); };
  const create = useConfirmedMutation({ title: "Draft this bonus credit note?", content: "It is created as a draft and still has to be approved before it reaches the retailer’s ledger.", okText: "Yes, draft it" }, {
    mutationFn: () => api.post('/credit-notes/bonus', {
      // `earnedFinancialYear` is the field now; `financialYear` stays for an older server, where it meant the same.
      retailerId, amount, earnedFinancialYear: financialYear, financialYear, reason: reason.trim() || undefined,
    }),
    onSuccess: () => {
      // Maker ≠ checker: the drafter cannot approve their own bonus (MAKER_CHECKER_SAME_USER).
      message.success('Turnover bonus drafted — another Admin or a Super Admin must approve & post it');
      reset();
      onCreated();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not draft the bonus'),
  });

  const valid = !!retailerId && !!amount && amount > 0 && /^\d{4}-\d{2}$/.test(financialYear);
  // The picked dealer and where their ledger stands: outstanding is positive when they owe, negative when they hold credit.
  const picked: any = retailerId ? retailerPick.rowFor(retailerId) : null;
  const pickedName = picked ? (picked.companyName ?? picked.name ?? `#${picked.id}`) : '';
  const balance = Number(picked?.outstandingBalance ?? 0);
  const owes = Math.max(0, balance);
  const credit = Math.max(0, -balance);

  return (
    <Modal
      open={open}
      title="New turnover / volume bonus"
      onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}
      footer={
        <>
          <Button variant="default" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!valid} onClick={() => create.mutate()}>Draft bonus</Button>
        </>
      }
    >
      {/* WHO IT IS FOR and WHERE THEY STAND, before the amount: a bonus is decided against the dealer's own
          year, and the form named neither the dealer's balance nor what the draft becomes. */}
      {picked ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-brand-500/7 px-3 py-2.5 shadow-e1">
          <EntityAvatar name={pickedName} size={30} square />
          <div className="min-w-0">
            <div className="type-caption text-subtle">Bonus for</div>
            <div className="type-body-strong text-content">{pickedName}</div>
            {picked.code && <div className="type-caption text-muted">{picked.code}{picked.city ? ` · ${picked.city}` : ''}</div>}
          </div>
          <div className="ml-auto text-right">
            <div className="type-caption text-subtle">Their account now</div>
            <div className={cn('type-body-strong tabular-nums', owes > 0.005 ? 'text-danger-600 dark:text-danger-400' : credit > 0.005 ? 'text-success-700 dark:text-success-400' : 'text-content')}>
              {owes > 0.005 ? `${inr2(owes)} owed` : credit > 0.005 ? `${inr2(credit)} credit` : 'settled'}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-3 flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm text-content">
          <TrendingUp size={16} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
          <span>
            A financial credit a dealer earns for their year's business. It carries <b>no GST</b> and posts to their ledger as
            advance-in-credit — money off their next orders — once a second Admin or a Super Admin approves it.
          </span>
        </div>
      )}
      {/* What this draft will do, in its own figures. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        <BonusStat label="Credit to post" tint="bg-success-500/10" value={amount && amount > 0 ? inr2(amount) : '—'} hint="on approval" />
        <BonusStat label="For the year" tint="bg-violet-500/8" value={`FY ${financialYear}`} />
        <BonusStat label="Tax" tint="bg-surface-2" value="None" hint="a financial incentive, outside GST" />
      </div>
      <div className="space-y-3">
        <Field label="Retailer">
          <Select
            placeholder="Select a retailer" className="w-full"
            showSearch filterOption={false} onSearch={retailerPick.onSearch}
            value={retailerId} onChange={setRetailerId}
            options={retailers.map((r: any) => ({ value: String(r.id), label: `${r.code ? `${r.code} · ` : ''}${r.companyName ?? r.name ?? `#${r.id}`}` }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Bonus amount (₹)">
            <InputNumber
              min={1} className="w-full" value={amount} onChange={(v) => setAmount(v as number | null)}
              formatter={(v) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))} parser={(v) => (v || '').replace(/,/g, '')}
            />
          </Field>
          <Field label="Financial year">
            <Select className="w-full" value={financialYear} onChange={setFinancialYear}
              options={FY_OPTIONS.map((fy) => ({ value: fy, label: fy }))} />
          </Field>
        </div>
        <Field label="Reason / note (optional)">
          <Textarea rows={2} placeholder="e.g. Annual turnover incentive for exceeding target" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/** One figure of the bonus being drafted: sentence-case label over a full-contrast value, on its own tint. */
function BonusStat({ label, value, hint, tint }: { label: string; value: React.ReactNode; hint?: string; tint: string }) {
  return (
    <div className={cn('min-w-0 rounded-card border border-line p-2.5 shadow-e1', tint)}>
      <div className="type-caption text-subtle">{label}</div>
      <div className="mt-0.5 type-body-strong wrap-break-word text-content">{value}</div>
      {hint && <div className="type-caption text-muted">{hint}</div>}
    </div>
  );
}

/** Delegates to the shared StatCard so this page's KPIs match the rest of the app (see DispatchesPage).
 *  `value` is a node, not a string: a counted tile carries its own unit beside the figure. */
function Kpi({ title, value, sub, accent, icon, onClick, active }: { title: string; value: React.ReactNode; sub?: string; accent: string; icon?: React.ReactNode; onClick?: () => void; active?: boolean }) {
  return <StatCard label={title} value={value} hint={sub} tone={statTone(accent)} icon={icon} onClick={onClick} active={active} />;
}
